// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import File from "@insite/mobius/Icons/File";
import Send from "@insite/mobius/Icons/Send";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Block from "@store/Library/Components/Toolkit/Block";
import Link, { LinkPresentationProps } from "@store/MobiusOverrides/Link/Link";
import { LoadingOverlayProps } from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import Typography, { TypographyPresentationProps } from "@store/MobiusOverrides/Typography/Typography";
import CardListHeading from "@store/Overrides/Components/CardListHeading";
import { linkGetActionLinkProps } from "@store/Themes/common/props/link";
import { typographyGetNoResultsProps } from "@store/Themes/common/props/typography";
import { PRICE_LIST_ACTUAL_URL, PRICE_LIST_NEXT_URL } from "@store/Framework/Services/AccountService";
import {
    getAccountTermsForCustomer,
    isAccountTermsLoading,
    isAccountTermsLoadingPriceList,
} from "@store/Framework/Store/Store/Data/Accounts/AccountSelectors";
import loadCurrentAccountTerms from "@store/Framework/Store/Store/Data/Accounts/Handlers/LoadCurrentAccountTerms";
import loadPriceList from "@store/Framework/Store/Store/Data/Accounts/Handlers/LoadPriceList";
import Clickable from "@store/MobiusOverrides/Clickable/Clickable";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { FC, useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showCurrentTerms = "showCurrentTerms",
    showNextTerms = "showNextTerms",
    showActualPriceList = "showActualPriceList",
    showNextPriceList = "showNextPriceList",
}

type OwnProps = WidgetProps & {
    fields: {
        [fields.showActualPriceList]: boolean;
        [fields.showNextPriceList]: boolean;
    };
};

const mapStateToProps = (state: ApplicationState) => {
    const { currentTermsUrl, nextTermsUrl } = getAccountTermsForCustomer(state);
    const isLoading = isAccountTermsLoading(state);
    const isLoadingPriceList = isAccountTermsLoadingPriceList(state);

    return {
        currentTermsUrl,
        nextTermsUrl,
        isLoading,
        isLoadingPriceList,
    };
};

const mapDispatchToProps = {
    loadCurrentAccountTerms,
    loadPriceList,
};

export interface ProductDetailsAnchorMenuStyles {
    loadingOverlay?: LoadingOverlayProps;
    wrapper?: InjectableCss;
    documentLink?: LinkPresentationProps;
    documentButton?: LinkPresentationProps;
    documentButtonIcon?: IconPresentationProps;
    noDocumentText?: TypographyPresentationProps;
}

const fileLinkStyle = linkGetActionLinkProps(File);
const clickableStyle = linkGetActionLinkProps(Send);

export const productDetailsAnchorMenuStyles: ProductDetailsAnchorMenuStyles = {
    loadingOverlay: { minHeight: 80 },
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;

            > * {
                margin: ${pxToRem(4)} 0;
            }
        `,
    },
    documentLink: {
        ...fileLinkStyle,
        css: css`
            ${fileLinkStyle.css}
            margin-right: ${pxToRem(20)};
        `,
    },
    documentButton: {
        ...fileLinkStyle,
        css: css`
            ${fileLinkStyle.css}
            margin-right: ${pxToRem(20)};
        `,
    },
    documentButtonIcon: {
        ...clickableStyle.icon?.iconProps,
        css: css`
            ${clickableStyle.icon?.iconProps?.css}
            &:first-child,
            &:last-child {
                margin-right: 0.3em;
            }
        `,
    },
    noDocumentText: typographyGetNoResultsProps(),
};

const styles = productDetailsAnchorMenuStyles;

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const TermsDownloadButton: FC<Props> = ({
    fields: { showCurrentTerms, showNextTerms, showActualPriceList, showNextPriceList },
    currentTermsUrl,
    nextTermsUrl,
    isLoading,
    isLoadingPriceList,
    loadCurrentAccountTerms,
    loadPriceList,
}) => {
    const toasterContext = useContext(ToasterContext);
    const [currentlyLoadingPricelist, setCurrentlyLoadingPricelist] = useState("");

    if (!showCurrentTerms && !showNextTerms && !showActualPriceList && !showNextPriceList) {
        return null;
    }
    if (
        !isLoading &&
        ((showCurrentTerms && currentTermsUrl === undefined) || (showNextTerms && nextTermsUrl === undefined))
    ) {
        loadCurrentAccountTerms();
    }

    const onClickPriceList = (url: string) => {
        setCurrentlyLoadingPricelist(url);
        loadPriceList({
            url,
            onSuccess: result => {
                toasterContext.addToast({
                    body: siteMessage("Account_PriceList_RequestedWithSuccess"),
                    messageType: "success",
                });
            },
            onError: result => {
                toasterContext.addToast({
                    body: siteMessage("Account_PriceList_RequestError"),
                    messageType: "danger",
                });
            },
        });
    };

    return (
        <Block>
            <CardListHeading heading={translate("Terms and Conditions of Sales and Warranty")} hasNoResults={true} />
            <LoadingOverlay {...styles.loadingOverlay} loading={isLoading}>
                {currentTermsUrl === "" && nextTermsUrl === "" && !showActualPriceList && !showNextPriceList ? (
                    <Typography {...styles.noDocumentText}>{translate("No documents")}</Typography>
                ) : (
                    <StyledWrapper {...styles.wrapper}>
                        {showCurrentTerms && currentTermsUrl && currentTermsUrl !== "" && (
                            <Link {...styles.documentLink} href={currentTermsUrl} target="_new">
                                {translate("Current Terms")}
                            </Link>
                        )}
                        {showNextTerms && nextTermsUrl && nextTermsUrl !== "" && (
                            <Link {...styles.documentLink} href={nextTermsUrl} target="_new">
                                {translate("Upcoming Terms")}
                            </Link>
                        )}
                        {showActualPriceList && (
                            <Clickable
                                {...styles.documentButton}
                                onClick={() => {
                                    onClickPriceList(PRICE_LIST_ACTUAL_URL);
                                }}
                                disabled={isLoadingPriceList && currentlyLoadingPricelist === PRICE_LIST_ACTUAL_URL}
                                isLoading={isLoadingPriceList && currentlyLoadingPricelist === PRICE_LIST_ACTUAL_URL}
                            >
                                <Icon {...styles.documentButtonIcon} />
                                {translate("Current Price List Request")}
                            </Clickable>
                        )}
                        {showNextPriceList && (
                            <Clickable
                                {...styles.documentButton}
                                onClick={() => {
                                    onClickPriceList(PRICE_LIST_NEXT_URL);
                                }}
                                disabled={isLoadingPriceList && currentlyLoadingPricelist === PRICE_LIST_NEXT_URL}
                                isLoading={isLoadingPriceList && currentlyLoadingPricelist === PRICE_LIST_NEXT_URL}
                            >
                                <Icon {...styles.documentButtonIcon} />
                                {translate("Upcoming Price List Request")}
                            </Clickable>
                        )}
                    </StyledWrapper>
                )}
            </LoadingOverlay>
        </Block>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(TermsDownloadButton),
    definition: {
        allowedContexts: ["MyAccountPage"],
        group: "My Account",
        icon: "arrow-down-from-line",
        fieldDefinitions: [
            {
                name: fields.showCurrentTerms,
                displayName: "Show Current Terms",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
            },
            {
                name: fields.showNextTerms,
                displayName: "Show Next Terms",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
            },
            {
                name: fields.showActualPriceList,
                displayName: "Show Current Price List",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
            },
            {
                name: fields.showNextPriceList,
                displayName: "Show Next Price List",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
            },
        ],
    },
};

export default widgetModule;
